import React, { useEffect, useState } from 'react';
import { Ticket, MapPin, Calendar, ExternalLink, Sparkles, ChevronRight } from 'lucide-react';
import { getLandmarkUrl } from '../lib/landmarkImages';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return 'TBA';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'TBA';
  return d.toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

const trackClick = (e) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', 'ticketmaster_click', {
      event_slug: e.slug,
      event_title: e.title,
      event_venue: e.venue,
      event_category: e.category,
      transport_type: 'beacon',
    });
  } catch (_e) { /* silent */ }
};

const HeroCard = ({ e }) => {
  const img = e.image_url || getLandmarkUrl(e.slug || e.id);
  return (
    <a
      href={e.link}
      target="_blank"
      rel="sponsored noopener noreferrer"
      className="group relative col-span-2 md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto md:h-full rounded-2xl overflow-hidden bg-slate-900 shadow-lg hover:shadow-2xl transition-shadow"
      data-testid={`home-live-music-card-${e.slug || e.id}`}
      onClick={() => trackClick(e)}
    >
      <img
        src={img}
        alt={e.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-900/60 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white bg-rose-500 px-2 py-1 rounded">
          <Sparkles size={11} /> Featured
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/90 bg-white/15 backdrop-blur-sm px-2 py-1 rounded">
          {e.category === 'sport' ? 'Sport' : e.category === 'family' ? 'Family' : e.category === 'community' ? 'Theatre' : 'Live music'}
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <p className="text-white/70 text-sm mb-2 flex items-center gap-1.5">
          <Calendar size={14} /> {fmtDate(e.start_date)}
        </p>
        <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight">
          {e.title}
        </h3>
        {e.venue && (
          <p className="text-white/80 text-sm mt-2 flex items-center gap-1.5">
            <MapPin size={14} /> {e.venue}
          </p>
        )}
        <span className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-semibold text-sm transition-colors">
          Get tickets <ChevronRight size={14} />
        </span>
      </div>
    </a>
  );
};

const CompactCard = ({ e }) => {
  const img = e.image_url || getLandmarkUrl(e.slug || e.id);
  return (
    <a
      href={e.link}
      target="_blank"
      rel="sponsored noopener noreferrer"
      className="group relative aspect-[16/10] rounded-xl overflow-hidden bg-slate-900 shadow-sm hover:shadow-lg transition-shadow"
      data-testid={`home-live-music-card-${e.slug || e.id}`}
      onClick={() => trackClick(e)}
    >
      <img
        src={img}
        alt={e.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-900/40 to-transparent" />
      <div className="absolute top-2 left-2">
        <span className="text-[9px] font-bold uppercase tracking-wider text-white bg-rose-500 px-1.5 py-0.5 rounded">
          {e.category === 'sport' ? 'Sport' : e.category === 'family' ? 'Family' : e.category === 'community' ? 'Theatre' : 'Gig'}
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-[10px] text-white/70 uppercase tracking-wider mb-1">{fmtDate(e.start_date).replace(', ', ' · ')}</p>
        <h4 className="text-sm font-bold text-white leading-tight line-clamp-2">{e.title}</h4>
        {e.venue && (
          <p className="text-[11px] text-white/70 mt-1 truncate" title={e.venue}>
            {e.venue}
          </p>
        )}
      </div>
    </a>
  );
};

/**
 * Homepage tile: upcoming Ticketmaster + Skiddle events near Paisley.
 *
 * Premium design: dark section, radial glow, 1 hero card (2×2) + 4 compact
 * cards on desktop. On mobile, an extra 4 events are exposed via
 * `md:hidden` so scrollers see 9 total without cluttering desktop.
 *
 * Every click is affiliate-tagged (Ticketmaster CAMEFROM + Skiddle sktag)
 * and fires a GA4 `ticketmaster_click` event.
 *
 * The subtitle pulls the top 5 venues from the current inventory so it always
 * reflects what's actually on sale — never stale.
 */
const LiveMusicNearby = ({ limit = 9 }) => {
  const [items, setItems] = useState([]);
  const [venues, setVenues] = useState([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/homepage/live-music?limit=${limit}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const arr = Array.isArray(d?.items) ? d.items : (Array.isArray(d) ? d : []);
        setItems(arr);
        setVenues(Array.isArray(d?.top_venues) ? d.top_venues : []);
        setState(arr.length ? 'ready' : 'empty');
      })
      .catch(() => alive && setState('empty'));
    return () => { alive = false; };
  }, [limit]);

  if (state === 'empty') return null;

  const hero = items[0];
  // Desktop shows 1 hero + 4 compacts = 5 cards. Mobile continues past that
  // with 4 extra compacts (`md:hidden`) → 9 cards on mobile total.
  const compactVisible = items.slice(1, 5);
  const compactMobileExtras = items.slice(5, 9);

  const venueLine = venues.length
    ? venues.slice(0, 4).join(' · ')
    : 'SEC · Hydro · King Tut\'s · Barrowland';

  return (
    <section
      className="relative py-20 bg-gradient-to-br from-slate-950 via-[#1a0512] to-slate-950 text-white overflow-hidden"
      data-testid="home-live-music"
    >
      {/* Radial glow */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-rose-600/60 blur-[130px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-amber-500/20 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/20 text-rose-200 text-[11px] font-bold uppercase tracking-[0.18em]">
              <Ticket size={12} /> Live music &amp; events
            </div>
            <h2
              className="text-3xl md:text-5xl font-bold tracking-tight mt-3"
              data-testid="home-live-music-h2"
            >
              Tonight&apos;s in Paisley &amp; Glasgow
            </h2>
            <p className="text-sm md:text-base text-white/70 mt-2 max-w-2xl">
              Featuring <span className="text-white font-semibold">{venueLine}</span> — official Ticketmaster tickets, booked in seconds.
            </p>
          </div>
          <a
            href="/events"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors"
            data-testid="home-live-music-all"
          >
            All events <ChevronRight size={14} />
          </a>
        </div>

        {state === 'loading' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-live-music-skeleton">
            <div className="col-span-2 md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto md:h-full bg-white/5 animate-pulse rounded-2xl" />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[16/10] bg-white/5 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:auto-rows-fr"
            data-testid="home-live-music-list"
          >
            {hero && <HeroCard e={hero} />}
            {compactVisible.map((e) => <CompactCard key={e.id || e.slug} e={e} />)}
            {/* Mobile-only extras — hidden from md up so desktop stays 1+4. */}
            {compactMobileExtras.map((e) => (
              <div key={e.id || e.slug} className="md:hidden">
                <CompactCard e={e} />
              </div>
            ))}
          </div>
        )}

        {/* Trust bar — subtle reassurance under the grid */}
        <div className="mt-8 flex items-center justify-center gap-6 text-xs text-white/50 flex-wrap">
          <span>Official Ticketmaster partner</span>
          <span className="w-1 h-1 rounded-full bg-white/30" />
          <span>Prices, dates &amp; availability live</span>
          <span className="w-1 h-1 rounded-full bg-white/30" />
          <span>Secure checkout on Ticketmaster</span>
        </div>
      </div>
    </section>
  );
};

export default LiveMusicNearby;
